import { setFavorite } from '@/lib/api/reports-client.js';
import type { CatalogResponse } from '@/lib/api/types.js';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useRef, useState } from 'react';

interface ToggleContext {
  previousData: CatalogResponse | undefined;
  queryKey: readonly unknown[];
  tenantReportId: string;
}

export interface UseToggleFavoriteResult {
  toggle: (tenantReportId: string, currentValue: boolean) => void;
  pendingIds: ReadonlySet<string>;
}

export function useToggleFavorite(
  currentParams: Record<string, unknown> = {},
): UseToggleFavoriteResult {
  const queryClient = useQueryClient();
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  // Track in-flight ids to prevent duplicate concurrent calls for the same id
  const inFlightRef = useRef<Set<string>>(new Set());

  const mutation = useMutation<
    { is_favorite: boolean },
    Error,
    { tenantReportId: string; currentValue: boolean },
    ToggleContext
  >({
    mutationFn: ({ tenantReportId, currentValue }) => setFavorite(tenantReportId, !currentValue),

    onMutate: async ({ tenantReportId, currentValue }) => {
      const queryKey = ['catalog', currentParams] as const;
      await queryClient.cancelQueries({ queryKey });

      const previousData = queryClient.getQueryData<CatalogResponse>(queryKey);

      queryClient.setQueryData<CatalogResponse>(queryKey, (old) => {
        if (old === undefined) return old;
        return {
          ...old,
          items: old.items.map((item) =>
            item.tenant_report_id === tenantReportId
              ? { ...item, is_favorite: !currentValue }
              : item,
          ),
        };
      });

      return { previousData, queryKey, tenantReportId };
    },

    onError: (_err, _vars, context) => {
      if (context !== undefined && context.previousData !== undefined) {
        queryClient.setQueryData(context.queryKey, context.previousData);
      }
    },

    onSettled: (_data, _err, _vars, context) => {
      if (context !== undefined) {
        inFlightRef.current.delete(context.tenantReportId);
        setPendingIds((prev) => {
          const next = new Set(prev);
          next.delete(context.tenantReportId);
          return next;
        });
      }
      void queryClient.invalidateQueries({ queryKey: ['catalog'] });
    },
  });

  const toggle = useCallback(
    (tenantReportId: string, currentValue: boolean) => {
      // Single-flight guard: skip if already in flight for this id
      if (inFlightRef.current.has(tenantReportId)) return;
      inFlightRef.current.add(tenantReportId);
      setPendingIds((prev) => {
        const next = new Set(prev);
        next.add(tenantReportId);
        return next;
      });
      mutation.mutate({ tenantReportId, currentValue });
    },
    [mutation],
  );

  return { toggle, pendingIds };
}
